/**
 * TourButton Component
 *
 * Displays a "?" icon in the Header that opens the guided tour for the current route.
 * Shows a green badge dot when the tour for the current route hasn't been seen yet.
 * Hidden (via visibility:hidden) rather than null when no steps are available,
 * to preserve the Space gap in the Header.
 */

import { Button, Tooltip, Badge } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTour } from '@reactour/tour';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTourContext } from '@/context/TourContext';
import { getTourStepsForRoute } from '@/tours';

export function TourButton() {
  const { setIsOpen } = useTour();
  const location = useLocation();
  const { hasPermission, hasFeature, user } = useAuth();
  const { hasSeen } = useTourContext();

  const isMobile = window.innerWidth < 768;

  const steps = getTourStepsForRoute(location.pathname, {
    hasPermission: hasPermission as (permission: string) => boolean,
    hasFeature,
    isMobile,
    isSuperadmin: !!user?.is_superadmin,
  });

  const hasSteps = steps.length > 0;
  const isNew = hasSteps && !hasSeen(location.pathname);

  const handleClick = () => {
    if (hasSteps) {
      setIsOpen(true);
    }
  };

  return (
    <div
      style={{
        visibility: hasSteps ? 'visible' : 'hidden',
        pointerEvents: hasSteps ? 'auto' : 'none',
        // preserve layout space even when hidden
        display: 'inline-flex',
      }}
    >
      <Tooltip title="Tour guiado desta página" placement="bottom">
        <Badge dot color="green" offset={[-4, 4]} style={{ display: isNew ? undefined : 'none' }}>
          <Button
            type="text"
            icon={<QuestionCircleOutlined />}
            onClick={handleClick}
            aria-label="Iniciar tour guiado"
            style={{ fontSize: '18px' }}
          />
        </Badge>
      </Tooltip>
    </div>
  );
}
